import { cn } from "@-/com.npmjs.cn";
import plurCount from "@-/com.npmjs.plur-count";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import {
  Surface,
  SurfaceContent,
  SurfaceHeader,
  SurfaceTitle,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { ReactNode } from "react";
import { ChinaMapPlaceGlyph } from "#china-map-place-glyph";
import type { ChinaMapPlaceView, ChinaMapProvinceView } from "#china-map-places";
import { ChinaMapPlaceChips } from "#china-map-travel-cards";

/** How many upcoming places the sheet lists. */
const MAX_UPCOMING_PLACES = 8;

function SheetSection({ children, title }: { children: ReactNode; title: string }) {
  return (
    <View className="gap-2 px-3 py-4">
      <Text className="text-lg font-semibold text-foreground">{title}</Text>
      {children}
    </View>
  );
}

function countUnlocked(views: readonly ChinaMapPlaceView[]): number {
  return views.filter((view) => view.unlock.status === "unlocked").length;
}

/**
 * One upcoming place: the glyphs of its name (learned ones semibold), its official name and
 * province, and when it unlocks: the level that teaches its last character, and how many lessons
 * away that is.
 */
function UpcomingPlaceRow({
  isFirst,
  onSelect,
  view,
}: {
  isFirst: boolean;
  onSelect: (view: ChinaMapPlaceView) => void;
  view: ChinaMapPlaceView;
}) {
  const { characters, levelIndex } = view.unlock;
  const unlockLevel = levelIndex === null ? 1 : levelIndex + 1;
  const lessons =
    view.lessonsUntilUnlock === null ? null : plurCount(view.lessonsUntilUnlock, "lesson");
  const isProvince = view.place === view.province;
  return (
    <Pressable
      accessibilityLabel={`${view.displayName}, unlocks at level ${unlockLevel}${lessons === null ? "" : ` in ${lessons}`}`}
      accessibilityRole="button"
      className={cn(
        "flex-row items-center gap-3 px-3 py-2.5 active:bg-accent",
        !isFirst && "border-t border-border/60",
      )}
      onPress={() => onSelect(view)}
      testID={`china-map-upcoming-place-${view.place.adcode}`}
    >
      <View className="flex-row">
        {Array.from(view.displayName, (glyph, index) => (
          <ChinaMapPlaceGlyph
            className="text-2xl"
            glyph={glyph}
            // eslint-disable-next-line react/no-array-index-key -- place-name glyphs retain reading order
            key={index}
            pinyin={view.place.pinyin[index] ?? ""}
            studied={characters[index]?.studied === true}
          />
        ))}
      </View>
      <Text className="flex-1 text-xs text-muted-foreground" numberOfLines={2}>
        {isProvince ? view.place.name : `${view.place.name} · ${view.province.name}`}
      </Text>
      <View className="items-end">
        <Text className="text-sm font-semibold">Unlocks at Level {unlockLevel}</Text>
        {lessons === null ? null : (
          <Text className="text-xs text-muted-foreground">(in {lessons})</Text>
        )}
      </View>
      <Icon className="text-muted-foreground" name="MdChevronRight" size={20} />
    </Pressable>
  );
}

/** The places the learner will unlock soonest, in a surface titled for the open map. */
function UpcomingPlacesSurface({
  onSelect,
  title,
  views,
}: {
  onSelect: (view: ChinaMapPlaceView) => void;
  /** `ChinaMapRegionCopy.upcomingPlacesTitle`: "Upcoming Places". */
  title: string;
  views: readonly ChinaMapPlaceView[];
}) {
  return (
    <Surface testID="china-map-upcoming-places">
      <SurfaceHeader>
        <SurfaceTitle className="pb-2">{title}</SurfaceTitle>
      </SurfaceHeader>
      <SurfaceContent>
        {views.slice(0, MAX_UPCOMING_PLACES).map((view, index) => (
          <UpcomingPlaceRow
            isFirst={index === 0}
            key={view.place.adcode}
            onSelect={onSelect}
            view={view}
          />
        ))}
      </SurfaceContent>
    </Surface>
  );
}

/**
 * Before the first journey: the places the learner can start in, then the ones they will unlock
 * soonest. The map does not color progress, so these lists are how the learner finds them. With no
 * place unlocked yet, the places they will unlock first are the whole sheet.
 */
export function ChinaMapChooseStartSheetContent({
  availablePlaces,
  onSelect,
  quests,
  upcomingPlaces,
  upcomingPlacesTitle,
}: {
  availablePlaces: readonly ChinaMapPlaceView[];
  onSelect: (view: ChinaMapPlaceView) => void;
  /** The quests offered before the first journey: the global ones. */
  quests: ReactNode;
  upcomingPlaces: readonly ChinaMapPlaceView[];
  upcomingPlacesTitle: string;
}) {
  if (availablePlaces.length === 0) {
    return (
      // Surfaces sit 4px from the sheet's edges and from each other; text keeps its 16px inset.
      <View className="gap-1 px-1 pt-1" testID="china-map-choose-start">
        <View className="gap-1 px-3 pb-2">
          <Text className="text-2xl font-semibold text-foreground">Unlock your first city</Text>
          <Text className="text-sm text-muted-foreground">
            Learn every character in a city&apos;s name to start your journey there.
          </Text>
        </View>
        {quests}
        {upcomingPlaces.length === 0 ? null : (
          <UpcomingPlacesSurface
            onSelect={onSelect}
            title={upcomingPlacesTitle}
            views={upcomingPlaces}
          />
        )}
      </View>
    );
  }
  return (
    // Surfaces sit 4px from the sheet's edges and from each other; text keeps its 16px inset and
    // 20px from the surfaces beside it.
    <View className="gap-1 px-1 pt-1" testID="china-map-choose-start">
      <View className="gap-1 px-3 pb-4">
        <Text className="text-2xl font-semibold text-foreground">Choose a city to start!</Text>
        <Text className="text-sm text-muted-foreground">
          You can only travel to cities once you unlock all the characters in their name.
        </Text>
      </View>
      {quests}
      <SheetSection title="Available Now">
        <ChinaMapPlaceChips
          onSelect={onSelect}
          testIdPrefix="china-map-start"
          views={availablePlaces}
        />
      </SheetSection>
      {upcomingPlaces.length === 0 ? null : (
        <UpcomingPlacesSurface
          onSelect={onSelect}
          title={upcomingPlacesTitle}
          views={upcomingPlaces}
        />
      )}
    </View>
  );
}

/**
 * The unselected map's itinerary: where the learner can go, the quests offered, and what unlocks
 * next.
 */
export function ChinaMapOverviewSheetContent({
  destinations,
  onSelect,
  placesLabel,
  provinces,
  provincesLabel,
  quests,
  upcomingPlaces,
  upcomingPlacesTitle,
  visitedCount,
}: {
  /** Unlocked or visited places other than the current one, nearest first. */
  destinations: readonly ChinaMapPlaceView[];
  onSelect: (view: ChinaMapPlaceView) => void;
  /** What this map's places and top-level regions are called (see `ChinaMapRegionCopy`). */
  placesLabel: string;
  provinces: readonly ChinaMapProvinceView[];
  provincesLabel: string;
  /** The quests offered here: the city's own (on the learner's map) and the global ones. */
  quests: ReactNode;
  upcomingPlaces: readonly ChinaMapPlaceView[];
  upcomingPlacesTitle: string;
  /** Places visited on this map. */
  visitedCount: number;
}) {
  const cities = provinces.flatMap((province) => province.children);
  return (
    // Surfaces sit 4px from the sheet's edges and from each other; text keeps its 16px inset and
    // 20px from the surfaces beside it.
    <View className="gap-1 px-1 pt-1" testID="china-map-overview-card">
      <View className="gap-2 px-3 pb-4">
        <Text
          className="text-2xl font-semibold text-foreground"
          numberOfLines={1}
          testID="china-map-itinerary-title"
        >
          Itinerary
        </Text>
        {destinations.length === 0 ? (
          <Text className="text-sm text-muted-foreground">
            Unlock all the characters in a place&apos;s name to travel there.
          </Text>
        ) : (
          <ChinaMapPlaceChips
            onSelect={onSelect}
            testIdPrefix="china-map-destination"
            views={destinations}
          />
        )}
      </View>
      {quests}
      {upcomingPlaces.length === 0 ? null : (
        <UpcomingPlacesSurface
          onSelect={onSelect}
          title={upcomingPlacesTitle}
          views={upcomingPlaces}
        />
      )}
      <View className="flex-row gap-6 px-3 pt-4">
        <View>
          <Text className="text-xl font-semibold text-foreground">
            {countUnlocked(provinces)}/{provinces.length}
          </Text>
          <Text className="text-xs text-muted-foreground">{provincesLabel} unlocked</Text>
        </View>
        <View>
          <Text className="text-xl font-semibold text-foreground">
            {countUnlocked(cities)}/{cities.length}
          </Text>
          <Text className="text-xs text-muted-foreground">{placesLabel} unlocked</Text>
        </View>
        <View>
          <Text className="text-xl font-semibold text-foreground">{visitedCount}</Text>
          <Text className="text-xs text-muted-foreground">Visited</Text>
        </View>
      </View>
    </View>
  );
}
